import React, { useEffect, useMemo } from 'react';
import { withFormik } from 'formik';
import * as Yup from 'yup';

import { restRequest } from '../../../Helpers/RequestHelper';

import {
  ValidateRequired,
  ValidateNumber,
  required,
  moreThanZero,
  ValidateMax50,
  ValidateMax4,
} from '../../../Helpers/Validator';
import Field from '../Field/Field';
import Form from '../Form';
import FormSection from '../FormSection/FormSection';
import FormAction from '../FormAction';
import ErrorFocus from '../ErrorFocus';
import { checkError } from '../../../Helpers/AuthHelper';
import { fetchCurrency } from '../../../Helpers';
import { t } from 'i18next';
import { debounce } from 'lodash';

const formEnhancer = withFormik({
  validationSchema: (props) =>
    Yup.object().shape({
      name: ValidateRequired,
      currencyCode: required(ValidateMax50),
      symbol: required(ValidateMax4),
      exchangeRate:
        // props.type === 'add'
        required(moreThanZero(ValidateNumber)),
      // :Yup.mixed().notRequired(),
    }),
  enableReinitialize: true, // 👈 this line fixes it

  mapPropsToValues: (props) => {
    return {
      // 👇 Formik will use these as initialValues
      name: props.initialValues?.name || '',
      currencyCode: props.initialValues?.currencyCode || '',
      symbol: props.initialValues?.symbol || '',
      exchangeRate: props.initialValues?.exchangeRate,
      currencyId: props.currencyId || '',
      id:
        props.initialValues?.global_currency_id ||
        props.initialValues?.id ||
        props.global_currency_id || // ✅ read directly here
        '',
      type: props.type || '',
      onSubmit: props.onSubmit || (() => {}),
      fromInvoice: props.fromInvoice || false,
    };
  },

  handleSubmit: (values, bag) => {
    handleFormSubmit(values, bag);
  },
});

const CurrencyForm = ({
  values: { name, currencyId, currencyCode, symbol, exchangeRate },
  handleSubmit,
  handleChange,
  handleBlur,
  setFieldValue,
  setFieldTouched,
  setValues,
  submitCount,
  isSubmitting,
  errors,
  touched,
  onSubmit,

  history,
  type,
  closeEditmodal,
  currencyListFromDB = [],
  global_currencies = [],
  currency: selectedCurrency,
}) => {
  const [isBaseCurrency, setIsBaseCurrency] = React.useState(false);
  // Handle currency selection change
  const handleCurrencyChange = (
    option,
    currencyList,
    setValues,
    setFieldValue
  ) => {
    if (option) {
      const selected =
        currencyList.find(
          (c) => c.currency_code === option.value || c.id === option.value
        ) || {};

      setValues((prevValues) => ({
        ...prevValues,
        currencyId: selected.currency_code || selected.id,
        name: selected.name || '',
        currencyCode: selected.currency_code || '',
        symbol: selected.symbol || '',
        // exchangeRate: selected.exchange_rate || 1,
      }));
      setFieldValue('value', selected.currency_code || selected.id);
      setFieldValue('id', selected.id);
      setFieldValue('currencyId', selected.currency_code || selected.id);
      setFieldValue('name', selected.name || '');
      setFieldValue('currencyCode', selected.currency_code || '');
      setFieldValue('symbol', selected.symbol || '');
      // setFieldValue('exchangeRate', selected.exchange_rate || 1);

      // Update the value of the dropdown field
      setFieldValue('value', selected.currency_code || selected.id);
    }
  };

  // Get the current value for the dropdown
  const getCurrentCurrencyValue = () => {
    if (currencyId) {
      return currencyId;
    }

    if (type === 'edit' && selectedCurrency) {
      return selectedCurrency.currency_code || selectedCurrency.id;
    }

    return '';
  };

  // Get the options for the dropdown
  const getCurrencyOptions = () => {
    return (type === 'edit' ? global_currencies : currencyListFromDB).map(
      (currency) => ({
        value: currency.currency_code || currency.id,
        label: `${currency.currency_code} - ${currency.symbol} - ${currency.name}`,
      })
    );
  };

  useEffect(() => {
    type === 'edit' &&
      fetchCurrency().then((res) =>
        setIsBaseCurrency(res.id === selectedCurrency?.id)
      );
  }, [type]);

  const debouncedSetRate = useMemo(
    () =>
      debounce((rate) => {
        if (rate < 0.0000000001) rate = 1;
        if (rate > 9999) rate = 9999;
        setFieldValue('exchangeRate', rate);
      }, 500),
    [setFieldValue]
  );

  return (
    <Form className="currency-form" onSubmit={handleSubmit}>
      <FormSection title={''}>
        <div className="d-flex" style={{ gap: '50px' }}>
          {type === 'edit' ? (
            <Field
              size="large"
              name={t('Settings.currency')}
              id="currencyId"
              value={getCurrentCurrencyValue()}
              options={getCurrencyOptions()}
              dropdownValue="value"
              dropdownLabel="label"
              handleChange={(option) =>
                handleCurrencyChange(
                  option,
                  global_currencies,
                  setValues,
                  setFieldValue
                )
              }
              onBlur={() => setFieldTouched('currencyId', true)}
              type="dropdown"
              clearable={false}
              required
              // disabled={type === 'edit'}
              error={touched.currencyId && errors.currencyId}
            />
          ) : (
            <Field
              size="large"
              name={t('Settings.currency')}
              id="currencyId"
              value={currencyId}
              options={getCurrencyOptions()}
              dropdownValue="value"
              dropdownLabel="label"
              handleChange={(option) =>
                handleCurrencyChange(
                  option,
                  currencyListFromDB,
                  setValues,
                  setFieldValue
                )
              }
              onBlur={() => setFieldTouched('currencyId', true)}
              type="dropdown"
              // disabled={type === 'edit'}
              clearable={false}
              required
              error={touched.currencyId && errors.name}
            />
          )}
          <ul className="currency-info">
            <li>
              <span>{t('common.currencyCode')}:</span>
              <strong>{currencyCode}</strong>
            </li>
            <li>
              <span>{t('Settings.symbol')}:</span>
              <strong>{symbol}</strong>
            </li>
          </ul>
        </div>
        {/* <Field
          size="large"
          name={t('Settings.currency_code')}
          value={
            currencyCode
          }
          id="currencyCode"
          required
          handleChange={handleChange}
          onBlur={handleBlur}
          disabled={type === 'edit'}
          error={touched.currencyCode && errors.currencyCode}
        /> */}

        {/* <Field
          size="large"
          name={t('Settings.symbol')}
          value={symbol}
          id="symbol"
          required
          disabled={type === 'edit'}
          handleChange={handleChange}
          onBlur={handleBlur}
          error={touched.symbol && errors.symbol}
        /> */}

        <Field
          size="large"
          type="number"
          value={isBaseCurrency ? 1 : exchangeRate}
          name={t('Settings.exchange_rate')}
          id="exchangeRate"
          decimalScale={6}
          required
          onValueChange={(value) => {
            debouncedSetRate(value.floatValue);
          }}
          onBlur={handleBlur}
          error={
            (touched.exchangeRate || submitCount > 0) && errors.exchangeRate
          }
          disabled={isBaseCurrency}
        />

        <FormAction
          disabled={isSubmitting}
          onCancel={
            () => closeEditmodal()
            // history.goBack()
          }
        />
      </FormSection>
      <ErrorFocus />
    </Form>
  );
};

const handleFormSubmit = (
  values,
  {
    setSubmitting,
    props: {
      requestType,
      requestUrl,
      history,
      fromInvoice,
      onSubmit,
      successMessage,
      handleToast,
      type,
    },
  }
) => {
  const submitRequestData = () => {
    const { name, currencyCode, symbol, id, exchangeRate } = values;
    switch (type) {
      case 'edit':
        return {
          symbol: symbol,
          name: name,
          currency_code: currencyCode,
          global_currency_id: id,
          exchange_rate: exchangeRate,
        };
      case 'add':
      default:
        return {
          name: name,
          currency_code: currencyCode,
          symbol: symbol,
          exchange_rate: exchangeRate,
          global_currency_id: id,
        };
    }
  };
  // console.log(this.props, "this.props>>>")

  restRequest(requestType, requestUrl, submitRequestData())
    .then((res) => {
      if (fromInvoice) {
        onSubmit(res);
      }
      successMessage();

      // history.push('/r');
    })
    .catch((error) => {
      checkError(error, handleToast);
      //handleToast(error, "error")
      setSubmitting(false);
    });
};

export default formEnhancer(CurrencyForm);
